import {Injectable} from '@angular/core';
import {Subscription} from 'rxjs';
import {skip} from 'rxjs/operators';
import {MmKeyboardStateService} from './keyboard-state.service';

const PANE_SELECTOR = '.mydocma-mm-fault-pane-dialog .mm-pane, .right-pane-container .mm-pane';
const TAB_CONTENT_SELECTOR = '.mat-tab-body-active .mat-tab-body-content';

// The fault DV swaps its scroller when the keyboard class flips: closed, .mat-tab-body-content scrolls
// the active tab; open, the freed height chain makes .mm-pane the scroller and the tab scroller inert.
// The offsets map 1:1 (header and tab bar occupy the same rows in both modes), but neither element
// inherits the other's position — without a transfer the content snaps to the top and focus-reveal
// drags the field back, a visible double jump. State subscribers run before the class is toggled, so
// the outgoing scroller still holds its position when captured here; the incoming scroller only
// overflows after the relayout, hence the write waits one frame. Must start before the focus-reveal
// service so the reveal's frame already sees the transferred position and stays a no-op.
@Injectable({providedIn: 'root'})
export class MmKeyboardScrollHandoffService {

  private stateSub: Subscription | null = null;

  constructor(private keyboardState: MmKeyboardStateService) {}

  start(): void {
    if (this.stateSub || typeof document === 'undefined') {
      return;
    }
    this.stateSub = this.keyboardState.keyboardOpen$.pipe(skip(1)).subscribe(open => this.transfer(open));
  }

  private transfer(open: boolean): void {
    const pane = document.querySelector<HTMLElement>(PANE_SELECTOR);
    const tabContent = pane ? pane.querySelector<HTMLElement>(TAB_CONTENT_SELECTOR) : null;
    if (!pane || !tabContent) {
      return;
    }
    if (open) {
      // scroll-lock writes overscroll-behavior:contain inline on the tab scroller while it is the live
      // one (keyboard closed); the write outlives this swap and beats the scss auto override, and
      // Chromium then kills the gesture in the inert tab scroller instead of chaining to the pane —
      // the form freezes. Cleared on every open flip, all tab bodies (the stale write can sit on a
      // tab that is not active at flip time).
      pane.querySelectorAll<HTMLElement>('.mat-tab-body-content').forEach(el => el.style.removeProperty('overscroll-behavior'));
    }
    const from = open ? tabContent.scrollTop : pane.scrollTop;
    if (from <= 0) {
      return;
    }
    requestAnimationFrame(() => {
      (open ? pane : tabContent).scrollTop = from;
    });
  }
}
